import AttachCloudDetails, { AttachCloudDetailsCloudConfig } from "components/AttachCloud/Details";
import { EmptystateLinkColored, EmptystateMagnetColored } from "components/icons/generated";
import Counter from "ds/components/Counter";
import EmptyState from "ds/components/EmptyState";
import { useToggle } from "hooks/useToggle";
import { CLOUD_INTEGRATIONS, CloudIntegrationAttachment } from "shared/CloudIntegration/types";
import { StackAwsIntegrationAttachment } from "types/generated";
import CollapsibleSection from "ds/components/CollapsibleSection";

import styles from "./styles.module.css";

type AttachedCloudIntegrationsSectionProps = {
  title: string;
  integrations: Array<CloudIntegrationAttachment>;
  cloudConfig: AttachCloudDetailsCloudConfig;
  stackId: string;
  readOnly: boolean;
  onSuccessDetach?: (type: CLOUD_INTEGRATIONS) => void;
  isAutoAttached?: boolean;
  stackLabels?: string[];
  refetchQueries?: string[];
};

const getIntegrationType = (integration: CloudIntegrationAttachment) => {
  if (integration.__typename === "StackAzureIntegrationAttachment") {
    return CLOUD_INTEGRATIONS.Azure;
  } else if (integration.__typename === "StackAwsIntegrationAttachment") {
    return CLOUD_INTEGRATIONS.AWS;
  } else if (integration.__typename === "StackIntegrationGcp") {
    return CLOUD_INTEGRATIONS.GCP;
  }
  return CLOUD_INTEGRATIONS.AWS;
};

const AttachedCloudIntegrationsSection = ({
  title,
  integrations,
  cloudConfig,
  stackId,
  readOnly,
  onSuccessDetach,
  isAutoAttached,
  stackLabels,
  refetchQueries,
}: AttachedCloudIntegrationsSectionProps) => {
  const [isCollapsed, handleToggle] = useToggle(integrations.length === 0);

  return (
    <CollapsibleSection
      isCollapsed={isCollapsed}
      onToggle={handleToggle}
      title={title}
      titleSuffix={<Counter count={integrations.length} size="small" />}
      ariaLevel={2}
      ariaLabel={title}
      contentGap="large"
      className={styles.collapsibleSection}
    >
      {integrations.map((integration, index) => {
        const integrationType = getIntegrationType(integration);
        return (
          <AttachCloudDetails
            key={(integration as StackAwsIntegrationAttachment).id ?? `${integrationType}-${index}`}
            cloudConfig={cloudConfig}
            attachedIntegrationType={integrationType}
            attachedIntegration={integration}
            entityId={stackId}
            refetchQueries={refetchQueries}
            readOnly={readOnly}
            onSuccessDetach={() => onSuccessDetach?.(integrationType)}
            stackLabels={stackLabels}
          />
        );
      })}
      {integrations.length === 0 && (
        <EmptyState
          className={styles.emptyState}
          icon={isAutoAttached ? EmptystateMagnetColored : EmptystateLinkColored}
          title={
            isAutoAttached ? "No auto-attached integrations yet" : "No attached integrations yet"
          }
          caption={
            isAutoAttached
              ? "This is where auto-attached integrations will appear. Use autoattach label to attach integrations to this resource."
              : "This is where integrations that you have directly attached to this resource will appear."
          }
        />
      )}
    </CollapsibleSection>
  );
};

export default AttachedCloudIntegrationsSection;
